import React, { useState, useEffect } from 'react';
import { Building2, TrendingUp, Users, DollarSign, ListChecks, Clock, CheckCircle, AlertCircle, PlusCircle, ArrowRight } from 'lucide-react';
import { getJVProperties, JVPropertyResponse } from '../../utils/api';
import { useNavigate } from 'react-router-dom';

interface DashboardStats {
  totalSubmissions: number;
  pending: number;
  underReview: number;
  approved: number;
  rejected: number;
  totalValue: number;
  totalLeads: number;
}

export function DashboardView({ isAdmin = false }: { isAdmin?: boolean }) {
  const [submissions, setSubmissions] = useState<JVPropertyResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalSubmissions: 0,
    pending: 0,
    underReview: 0,
    approved: 0,
    rejected: 0,
    totalValue: 0,
    totalLeads: 0,
  });

  // Helper function to parse currency strings to numbers
  const parseCurrency = (value: any): number => {
    if (!value) return 0;
    if (typeof value === 'number') return value;
    // Remove $, commas, and any other non-numeric characters except decimal point
    const cleaned = String(value).replace(/[$,]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? 0 : num;
  };

  useEffect(() => {
    loadDashboardData();

    // Auto-refresh dashboard every 30 seconds
    const interval = setInterval(() => {
      loadDashboardData();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const data = await getJVProperties();
      setSubmissions(data);

      // Calculate real stats
      const calculatedStats: DashboardStats = {
        totalSubmissions: data.length,
        pending: data.filter(s => s.status?.toLowerCase() === 'pending').length,
        underReview: data.filter(s => s.status?.toLowerCase() === 'under_review').length,
        approved: data.filter(s => s.status?.toLowerCase() === 'approved').length,
        rejected: data.filter(s => s.status?.toLowerCase() === 'rejected').length,
        totalValue: data.reduce((sum, s) => sum + parseCurrency(s.arv || s.estimatedValue), 0),
        totalLeads: 0, // TODO: Implement leads tracking
      };

      setStats(calculatedStats);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Get most recent 5 submissions for activity feed
  const recentSubmissions = submissions
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  const getStatusIcon = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return <CheckCircle className="w-5 h-5 text-green-600" />;
      case 'under_review':
        return <Clock className="w-5 h-5 text-blue-600" />;
      case 'pending':
        return <AlertCircle className="w-5 h-5 text-amber-600" />;
      case 'rejected':
        return <AlertCircle className="w-5 h-5 text-red-600" />;
      default:
        return <Building2 className="w-5 h-5 text-tdb-accent" />;
    }
  };

  const getStatusBgColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved':
        return 'bg-green-50';
      case 'under_review':
        return 'bg-blue-50';
      case 'pending':
        return 'bg-amber-50';
      case 'rejected':
        return 'bg-red-50';
      default:
        return 'bg-tdb-accent/10';
    }
  };

  const getRelativeTime = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 30) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-tdb-accent mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-tdb-primary">Dashboard</h1>
          <p className="text-gray-600 mt-1">Welcome back! Here's your business overview</p>
        </div>
        <div className="text-sm text-gray-500">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>

      {/* Stats Grid - REAL DATA */}
      <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
        <div className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow p-6 border-l-4 border-amber-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500 font-medium uppercase tracking-wide">JV Submissions</p>
              <p className="text-4xl font-bold text-tdb-primary mt-2">{stats.totalSubmissions}</p>
            </div>
            <div className="bg-amber-50 p-4 rounded-full">
              <Building2 className="w-8 h-8 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Recent JV Submissions - Full Width */}
        {/* Recent Activity - REAL DATA */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-tdb-primary flex items-center space-x-2">
              <Clock className="w-5 h-5 text-tdb-accent" />
              <span>Recent JV Submissions</span>
            </h2>
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.dispatchEvent(new CustomEvent('navigate', { detail: 'jv-portal' }));
              }}
              className="text-sm text-tdb-accent hover:text-tdb-secondary font-semibold flex items-center space-x-1"
            >
              <span>View All</span>
              <ArrowRight className="w-4 h-4" />
            </a>
          </div>

          {recentSubmissions.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-block p-6 bg-tdb-accent/10 rounded-full mb-4">
                <Building2 className="w-12 h-12 text-tdb-accent" />
              </div>
              <h3 className="text-lg font-bold text-gray-800 mb-2">No Submissions Yet</h3>
              <p className="text-gray-600 mb-6">Get started by submitting your first JV property</p>
              <button
                onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'jv-portal' }))}
                className="px-6 py-3 bg-tdb-accent text-white rounded-xl hover:bg-[#0ea5ef] transition-all font-semibold inline-flex items-center space-x-2"
              >
                <PlusCircle className="w-5 h-5" />
                <span>Submit Property</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              {recentSubmissions.map((submission) => (
                <button
                  key={submission.id}
                  onClick={() => window.dispatchEvent(new CustomEvent('navigate', {
                    detail: 'jv-portal'
                  }))}
                  className="w-full flex items-start space-x-3 pb-4 border-b border-gray-100 last:border-b-0 text-left hover:bg-gray-50 transition-colors rounded-lg p-2 -mx-2"
                >
                  <div className={`p-2 ${getStatusBgColor(submission.status)} rounded-lg`}>
                    {getStatusIcon(submission.status)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {submission.propertyStreet}{submission.propertyLine2 ? ` ${submission.propertyLine2}` : ''}
                    </p>
                    <p className="text-sm text-gray-600">
                      {submission.propertyCity}, {submission.propertyState} - ${parseCurrency(submission.arv || submission.estimatedValue).toLocaleString()}
                    </p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        submission.status?.toLowerCase() === 'approved' ? 'bg-green-100 text-green-700' :
                        submission.status?.toLowerCase() === 'under_review' ? 'bg-blue-100 text-blue-700' :
                        submission.status?.toLowerCase() === 'pending' ? 'bg-amber-100 text-amber-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {submission.status?.replace('_', ' ')}
                      </span>
                      <p className="text-xs text-gray-400">{getRelativeTime(submission.createdAt)}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

      {/* Quick Actions */}
      <div className="bg-gradient-to-r from-tdb-primary via-tdb-secondary to-tdb-primary rounded-2xl shadow-xl p-8 text-white">
        <h2 className="text-2xl font-bold mb-6">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'jv-portal' }))}
            className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl p-6 text-left transition-all transform hover:scale-105"
          >
            <Building2 className="w-8 h-8 mb-3" />
            <div className="font-bold text-lg">Submit JV Property</div>
            <div className="text-sm text-gray-300 mt-1">Add new joint venture property</div>
          </button>

          {!isAdmin && (
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('navigate', { detail: 'tax-delinquent-lists' }))}
              className="bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-xl p-6 text-left transition-all transform hover:scale-105"
            >
              <ListChecks className="w-8 h-8 mb-3" />
              <div className="font-bold text-lg">Purchase a Tax Delinquent List</div>
              <div className="text-sm text-gray-300 mt-1">Get a list with us</div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
